import RefreshIcon from "@mui/icons-material/CachedRounded";
import { Button, Stack } from "@mui/material";
import { useCallback, useMemo, useState } from "react";
import { useLocation } from "react-router";
import type { InvestorChangeRequestState } from "@entrilia/shared/api/workflowTypes";
import RecordCounter from "@entrilia/shared/components/filters/RecordCounter";
import { useGridDataSourceWithRange } from "@entrilia/shared/components/grid/gridHooks";
import SearchField from "@entrilia/shared/components/inputs/SearchField";
import { scrollGridToTop } from "@entrilia/shared/utilities/dataGridApiHelper";
import adminApi from "../../../api/adminApi";
import useClientContext from "../../../context/useClientContext";
import { pageRoutes } from "../../../routes";
import { getReferrerLocationState } from "../../../utilities/navigationHelper";
import ChangeRequestsGrid from "./ChangeRequestsGrid";

interface Props {
  stateFilter?: InvestorChangeRequestState;
}

const ChangeRequestsList = ({ stateFilter }: Props) => {
  const { clientCode } = useClientContext();
  const location = useLocation();

  const [searchTerm, setSearchTerm] = useState<string>("");

  const getDetailsPath = useCallback(
    (workflowId: string) => `/${clientCode}/${pageRoutes.investorRelations}/${pageRoutes.requests}/${workflowId}`,
    [clientCode]
  );

  const detailsNavigationState = useMemo(() => getReferrerLocationState(location), [location]);

  const columnVisibilityModel = useMemo(
    () => (stateFilter === undefined ? undefined : { workflowStatus: false }),
    [stateFilter]
  );

  const filterPayload = useMemo(
    () => ({
      state: stateFilter,
      referenceCode: searchTerm || undefined,
    }),
    [searchTerm, stateFilter]
  );

  const { gridApiRef, dataSource, totalRows, isLoading, isRefreshing, refreshDataSource } = useGridDataSourceWithRange(
    adminApi.searchInvestorChangeRequests,
    filterPayload
  );

  const handleSearch = (value: string) => {
    const search = value.toUpperCase().trim();
    if (search.length === 0 || search.length > 1) {
      scrollGridToTop(gridApiRef);
      setSearchTerm(search);
    }
  };

  return (
    <Stack spacing={2} sx={{ height: "100%" }}>
      <Stack spacing={2.5} direction="row" sx={{ px: 1, alignItems: "center", justifyContent: "space-between" }}>
        <RecordCounter records={totalRows ?? 0} hide={isLoading} />

        <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
          <SearchField debounceTimeMs={300} onSearch={handleSearch} placeholder="Search by Reference" />
          <Button
            variant="outlined"
            color="secondary"
            disabled={!totalRows || isLoading}
            loading={isRefreshing}
            startIcon={<RefreshIcon />}
            onClick={refreshDataSource}
          >
            Refresh
          </Button>
        </Stack>
      </Stack>
      <ChangeRequestsGrid
        apiRef={gridApiRef}
        dataSource={dataSource}
        getDetailsPath={getDetailsPath}
        detailsNavigationState={detailsNavigationState}
        columnVisibilityModel={columnVisibilityModel}
      />
    </Stack>
  );
};

export default ChangeRequestsList;
